import React, {
    useCallback,
    useState
} from 'react';
import PostsTableComponent from '../components/PostsTableComponent.jsx';
import RIQModal from '../../../components/RIQModal.jsx';

import {
    useUpdateProps
} from '../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import SingleGlanceMetricDisplay from '../components/SingleGlanceMetricDisplay.jsx';

import useListeningCurationFilterValues from './useListeningCurationFilterValues.js';

import {
    listeningDefinitions
} from '../components/enums/listeningDefinitions.jsx';

function findPosNegAttribute(listeningFilters) {
    if (listeningFilters.like) {
        return {
            key: 'like',
            name: 'Attribute',
            isPositive: true
        };
    }
    if (listeningFilters.dislike) {
        return {
            key: 'dislike',
            name: 'Attribute',
            isPositive: false
        };
    }
    if (listeningFilters.positiveEmotion) {
        return {
            key: 'positiveEmotion',
            name: 'Emotion',
            isPositive: true
        };
    }
    if (listeningFilters.negativeEmotion) {
        return {
            key: 'negativeEmotion',
            name: 'Emotion',
            isPositive: false
        };
    }
    if (listeningFilters.positiveBehavior) {
        return {
            key: 'positiveBehavior',
            name: 'Behavior',
            isPositive: true
        };
    }
    if (listeningFilters.negativeBehavior) {
        return {
            key: 'negativeBehavior',
            name: 'Behavior',
            isPositive: false
        };
    }
}

function ModalTitle({instanceId, overrides, titleOverride, count}) {
    // these are the filters shared by the whole page
    const generalMetricFilters = useListeningCurationFilterValues();

    // these are the filters specific to this modal
    const metricFilters = useListeningCurationFilterValues(instanceId, overrides);

    const {
        subTitle
    } = listeningDefinitions['use-content-modal'];

    const timePeriod = metricFilters.timePeriod;

    let title = 'Posts';
    if (timePeriod) {
        title += ` from ${RivalIQ.metrics.new.MetricFormatter.formatDateInterval(timePeriod.startDate, timePeriod.endDate, timePeriod.timezone)}`;
    }

    const listeningFilters = metricFilters.listening;
    const posNegAttribute = findPosNegAttribute(listeningFilters);

    if (titleOverride) {
        title = `${title} from ${titleOverride}`;
    } else {
        if (listeningFilters.phrase) {
            // Popular Terms:
            // Posts from [Date Range] for Term: [Term]
            title = `${title} for Term: ${listeningFilters.phrase}`;
        }

        // NOTE we only want to add a term from `searchTermsAnd` if filters differ
        if (generalMetricFilters.listening.searchTermsAnd !== listeningFilters.searchTermsAnd) {
            let searchTermsIndex = -1;
            for (let i = 0, length = _.size(listeningFilters.searchTermsAnd); i < length; i++) {
                if (!_.isEmpty(listeningFilters.searchTermsAnd[i])) {
                    searchTermsIndex = i;
                    break;
                }
            }

            if (searchTermsIndex >= 0) {
                const searchTerm = _.last(listeningFilters.searchTermsAnd[searchTermsIndex]);
                // Associated Terms:
                // Posts from [Date Range] for Term: [Term]
                title = `${title} for Term: ${searchTerm}`;
            }
        }

        if (!_.isEmpty(listeningFilters.sentiment) && !_.isEqual(generalMetricFilters.listening.sentiment, metricFilters.listening.sentiment)) {
            // Sentiment:
            // Positive Posts from [Date Range]
            // Negative Posts from [Date Range]
            title = `${listeningFilters.sentiment.join(', ')} ${title}`;
        }

        if (!_.isEmpty(listeningFilters.geo) && !_.isEqual(generalMetricFilters.listening.geo, metricFilters.listening.geo)) {
            // Author Demographics:
            // Posts from [Date Range] from [Geo]
            title = `${title} from ${_.head(listeningFilters.geo)}`;
        }

        if (!_.isEmpty(listeningFilters.gender) && !_.isEqual(generalMetricFilters.listening.gender, metricFilters.listening.gender)) {
            // Author Demographics:
            // Post from [Date Range] from [Gender] Authors
            const genderText = listeningFilters.gender === 'Unknown' ? 'Unknown Gender' : listeningFilters.gender;
            title = `${title} from ${genderText} Authors`;
        }

        if (posNegAttribute) {
            // Popular Emotions:
            // Posts from [Date Range] for Emotion: [Term]
            // Attributes:
            // Posts from [Date Range] for Attribute: [Term]
            // Behaviors:
            // Posts from [Date Range] for Behavior: [Term]
            const className = posNegAttribute.isPositive ? 'tlw-text-green-400' : 'tlw-text-red-400';
            title = <>{title} for {posNegAttribute.name}: <span className={className}>{listeningFilters[posNegAttribute.key]}</span></>;
        }

        if (listeningFilters.domain) {
            // Domains:
            // Posts from [Date Range] from [Domain]
            title = `${title} from ${listeningFilters.domain}`;
        }

        if (listeningFilters.hashtag) {
            // Hashtags:
            // Posts from [Date Range] for ['#hashtag]
            title = `${title} for #${listeningFilters.hashtag}`;
        }

        if (listeningFilters.author) {
            // Authors:
            // Posts from [Date Range] from [Handle]
            title = `${title} from ${listeningFilters.author}`;
        }

        if (listeningFilters.brand) {
            // Brands Mentioned:
            // Posts from [Date Range] that mentioned [Brand]
            title = `${title} that mentioned ${listeningFilters.brand}`;
        }

        if (listeningFilters.person) {
            // People Mentioned:
            // Posts from [Date Range] that mentioned [Person]
            title = `${title} that mentioned ${listeningFilters.person}`;
        }
    }

    return (
        <div>
            <div className='tlw-leading-10'>{title}</div>
            <p className='tlw-mb-0 tlw-text-md tlw-leading-8'>{subTitle}</p>
        </div>
    );
}

function ContentModal({instanceId, overrides, titleOverride, isModalVisible, onModalDismissed}) {
    const metricFilters = useListeningCurationFilterValues(instanceId, overrides);
    const interval = 'day';

    const glanceMetrics = ['count', 'netSentiment'];
    const nameOverrides = {
        count: 'Posts'
    };

    const startDate = metricFilters[RivalIQ.metrics.new.enums.DataSourceFilterGroupIds.timePeriod]?.startDate;
    const endDate = metricFilters[RivalIQ.metrics.new.enums.DataSourceFilterGroupIds.timePeriod]?.endDate;
    const isOneDayTimeperiod = startDate && endDate && (moment(endDate).diff(startDate, 'day') <= 1);

    const metricRequests = _.reduce(glanceMetrics, (memo, glanceMetric) => {
        memo[glanceMetric] = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: glanceMetric,
            filters: metricFilters
        };

        if (!isOneDayTimeperiod) {
            memo[`${glanceMetric}ByDay`] = {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                dimensions: [interval],
                measure: glanceMetric,
                filters: metricFilters
            };
        }

        return memo;
    }, {});

    return (
        <RIQModal
            title={<ModalTitle
                instanceId={instanceId}
                overrides={overrides}
                titleOverride={titleOverride} />}
            showModal={isModalVisible}
            onModalDismissed={onModalDismissed}>
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}
                fallback={<></>}
            >
                {
                    (metricResults) => (
                        <div className='tlw-grid tlw-grid-cols-1 tlw-gap-5 tlw-p-7.5 sm_tlw-grid-cols-2'>
                            {
                                _.map(glanceMetrics, metricName => {
                                    return <SingleGlanceMetricDisplay
                                        key={metricName}
                                        metricRequest={metricRequests[metricName]}
                                        metricResult={metricResults[metricName]}
                                        metricName={nameOverrides[metricName]}
                                        renderSparkLine={!isOneDayTimeperiod}
                                        metricResultForSparkLine={metricResults[`${metricName}ByDay`]}
                                    />;
                                })
                            }
                        </div>
                    )
                }
            </MetricLoadingView>
            <div className='tlw-overflow-x-auto'>
                <PostsTableComponent
                    instanceId={instanceId}
                    limit={20}
                    overrides={overrides}
                />
            </div>
        </RIQModal>
    );
}

export default function useContentModal(instanceId) {

    if (!instanceId) {
        // Don't allow this hook to be used without an instanceId. See #35241
        throw new Error('instanceId is required for useContentModal hook');
    }

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [titleOverride, setTitleOverride] = useState(null);
    const [overrides, setOverrides] = useState([]);

    const modalInstanceId = `${instanceId}-content-modal`;
    const propsSetter = useUpdateProps(modalInstanceId);

    const showContentModal = useCallback((metricFilterOverrides, titleOverride) => {
        propsSetter(metricFilterOverrides || {});
        setIsModalVisible(true);
        setTitleOverride(titleOverride);
        setOverrides(_.keys(metricFilterOverrides));
    }, [propsSetter]);


    const modalComponent = <ContentModal
        instanceId={modalInstanceId}
        isModalVisible={isModalVisible}
        onModalDismissed={() => setIsModalVisible(false)}
        overrides={overrides}
        titleOverride={titleOverride}
    />;

    return {
        showContentModal,
        ContentModal: modalComponent
    };
}
